import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Headset, Plus, X, CircleNotch, Bug, CheckCircle, Clock } from '@phosphor-icons/react';
import {
  MyTicketConversationButton,
  MyTicketConversationDrawer,
} from '@/components/support/MyTicketConversation';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getApiErrorMessage } from '@/lib/api';
import {
  createSupportTicket,
  fetchMyTickets,
  getLastFailure,
  clearLastFailure,
  type MyTicketSummary,
  type SupportCategory,
  type SupportStatus,
} from '@/lib/supportApi';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * "My support tickets" — the customer half of the support system.
 *
 * Visibility is REQUESTER-ONLY and enforced server-side: this page can only ever show tickets the
 * signed-in person raised, never their chambers'. See support.service.ts for why that is the
 * right line on a platform where a ticket description can name a client.
 */

const CATEGORIES: { value: SupportCategory; label: string; hint: string }[] = [
  { value: 'technical', label: 'Something is not working', hint: 'A page, document, email or feature is broken' },
  { value: 'data_recovery', label: 'Missing or wrong data', hint: 'Something that should be there is not' },
  { value: 'billing', label: 'Billing', hint: 'Invoices, plan or payment questions' },
  { value: 'security', label: 'Security or access', hint: 'Access problems or a security concern' },
  { value: 'other', label: 'Something else', hint: '' },
];

const STATUS_META: Record<SupportStatus, { label: string; className: string }> = {
  open: { label: 'Open', className: 'bg-info-bg text-info' },
  in_progress: { label: 'In progress', className: 'bg-warning-bg text-warning' },
  resolved: { label: 'Resolved', className: 'bg-success-bg text-success' },
  closed: { label: 'Closed', className: 'bg-muted text-muted-foreground' },
};

function StatusPill({ status }: { status: SupportStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.open;
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function fmt(s: string) {
  return new Date(s).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ─── New ticket ───────────────────────────────────────────────────────────────

function NewTicketModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  /**
   * Pre-fill from the last failure this session actually saw.
   *
   * This is the whole point of the `lastFailure` buffer: a barrister cannot be expected to know
   * what a request id is, and without one a support executive is searching by name through a
   * platform-wide ledger. Captured once at the moment the failure was shown (see
   * DocumentViewer), attached here silently.
   */
  const failure = getLastFailure();

  const [title, setTitle] = useState(failure ? 'A document would not open' : '');
  const [body, setBody] = useState(
    failure ? `What happened:\n${failure.message}\n\nWhat I was trying to do:\n` : '',
  );
  const [category, setCategory] = useState<SupportCategory>('technical');
  const [attachContext, setAttachContext] = useState(true);

  const mutation = useMutation({
    mutationFn: () =>
      createSupportTicket({
        title,
        body,
        category,
        ...(attachContext && failure
          ? {
              reportedErrorCode: failure.errorCode,
              ...(failure.correlationId ? { reportedCorrelationId: failure.correlationId } : {}),
              ...(failure.target
                ? { reportedTargetType: failure.target.type, reportedTargetId: failure.target.id }
                : {}),
            }
          : {}),
      }),
    onSuccess: () => {
      toast.success('Support ticket raised — we will reply here and by email.');
      clearLastFailure();
      queryClient.invalidateQueries({ queryKey: ['support', 'mine'] });
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not raise the ticket')),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-8">
      <ScrollLock />
      <div className="my-auto w-full max-w-xl rounded-xl border border-border bg-card p-6 shadow-pop">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-foreground">Contact support</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Tell us what went wrong. Only you and Jurivoxa support can see this ticket.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="support-title" className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Summary
            </label>
            <input
              id="support-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. A document will not open"
              className="h-9 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="support-category" className="mb-1.5 block text-xs font-medium text-muted-foreground">
              What kind of problem is it?
            </label>
            <select
              id="support-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as SupportCategory)}
              className="h-9 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="support-body" className="mb-1.5 block text-xs font-medium text-muted-foreground">
              What happened?
            </label>
            <textarea
              id="support-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="What were you doing, and what did you expect to happen?"
              className="w-full rounded-lg border border-border bg-background p-3 text-sm text-foreground focus:border-primary focus:outline-none"
            />
          </div>

          {/*
            Consent, not surveillance. The technical context is offered plainly and can be
            declined — and the panel says exactly what it contains, which is a code and a
            reference number, never the document or its contents.
          */}
          {failure && (
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <label className="flex items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={attachContext}
                  onChange={(e) => setAttachContext(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    <Bug className="h-3.5 w-3.5" /> Include technical details
                  </span>
                  <span className="mt-1 block">
                    Attaches the error reference from the failure you just saw
                    {failure.correlationId ? ` (${failure.correlationId.slice(0, 8)})` : ''} so support
                    can find it immediately. No document contents, case details or messages are included.
                  </span>
                </span>
              </label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={title.trim().length < 3 || body.trim().length < 1 || mutation.isPending}
            >
              {mutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : 'Send to support'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MySupportPage() {
  const navigate = useNavigate();
  const [newOpen, setNewOpen] = useState(false);
  /** The ticket whose conversation is open. Communication stays attached to ONE ticket — there
   *  is no separate inbox and no generic chat. */
  const [talkingTo, setTalkingTo] = useState<MyTicketSummary | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['support', 'mine'],
    queryFn: () => fetchMyTickets(),
  });

  const tickets: MyTicketSummary[] = data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Support"
        subtitle="Report a problem to Jurivoxa and track the reply. Only you can see your tickets."
        onBack={() => navigate(-1)}
        actions={
          <Button onClick={() => setNewOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> New ticket
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <Headset className="h-9 w-9 text-muted-foreground/50" />
            <div>
              <p className="text-sm font-medium text-foreground">No support tickets</p>
              <p className="mt-1 text-sm text-muted-foreground">
                If something is not working, raise a ticket and we will investigate.
              </p>
            </div>
            <Button variant="outline" onClick={() => setNewOpen(true)}>Contact support</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            /*
              The card is a container, not a button: it now holds TWO distinct actions. Opening
              the ticket is the stretched title link (`after:inset-0`), so the whole card is still
              one big hit target; the conversation action sits above it on its own z-layer. A
              button inside a button is invalid HTML and would have broken keyboard order.
            */
            <div
              key={t.id}
              className="relative flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition hover:border-border/80 hover:bg-muted/30 focus-within:border-primary/50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => navigate(`/me/support/${t.id}`)}
                    className="truncate rounded text-left text-sm font-semibold text-foreground after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t.title}
                  </button>
                  <StatusPill status={t.status} />
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{t.body}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    {t.status === 'resolved' || t.status === 'closed'
                      ? <CheckCircle className="h-3.5 w-3.5" />
                      : <Clock className="h-3.5 w-3.5" />}
                    {fmt(t.createdAt)}
                  </span>
                  {!!t._count?.messages && (
                    <span>
                      {t._count.messages} {t._count.messages === 1 ? 'reply' : 'replies'}
                    </span>
                  )}
                  {!!t.unansweredCount && (
                    <span className="font-medium text-primary">
                      {t.unansweredCount} new from support
                    </span>
                  )}
                </div>
              </div>

              {/* COMMUNICATION — the same conversation Support is looking at, for THIS ticket. */}
              <MyTicketConversationButton
                className="relative z-10"
                unanswered={t.unansweredCount ?? 0}
                messageCount={t._count?.messages ?? 0}
                onClick={() => setTalkingTo(t)}
              />
            </div>
          ))}
        </div>
      )}

      {talkingTo && (
        <MyTicketConversationDrawer
          ticketId={talkingTo.id}
          title={talkingTo.title}
          onClose={() => setTalkingTo(null)}
        />
      )}

      {newOpen && <NewTicketModal onClose={() => setNewOpen(false)} />}
    </div>
  );
}
